import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import { keyframes } from '@mui/system';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import logo from '@/assets/images/logo.png';

interface AuthLayoutProps {
  children: ReactNode;
}

// Floating animation for particles
const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
`;

const floatSlow = keyframes`
  0%, 100% {
    transform: translateY(0px) translateX(0px);
  }
  50% {
    transform: translateY(-30px) translateX(10px);
  }
`;

export function AuthLayout({ children }: AuthLayoutProps) {
  const { t } = useTranslation(['landing', 'auth']);
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const isRegisterPage = location.pathname === '/register';

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#F6F8FB' }}>
      {/* Header with Navigation */}
      <Box
        component="header"
        sx={{
          py: 2,
          px: { xs: 2, sm: 3 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1100,
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="NLPask"
                sx={{
                  height: { xs: 32, sm: 40 },
                  width: 'auto',
                }}
              />
            </Box>

            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                component={Link}
                to="/"
                color="inherit"
                size="small"
                sx={{ display: { xs: 'none', md: 'inline-flex' } }}
              >
                Home
              </Button>
              <Button
                component={Link}
                to="/about"
                color="inherit"
                size="small"
                sx={{ display: { xs: 'none', md: 'inline-flex' } }}
              >
                {t('landing:nav.about')}
              </Button>
              <Button
                component={Link}
                to="/features"
                color="inherit"
                size="small"
                sx={{ display: { xs: 'none', md: 'inline-flex' } }}
              >
                {t('landing:nav.features')}
              </Button>
              <Button
                component={Link}
                to="/faq"
                color="inherit"
                size="small"
                sx={{ display: { xs: 'none', md: 'inline-flex' } }}
              >
                {t('landing:nav.faq')}
              </Button>
              <LanguageSwitcher />
              {isRegisterPage && (
                <Button component={Link} to="/login" variant="outlined" size="small">
                  {t('auth:login.title')}
                </Button>
              )}
              {isLoginPage && (
                <Button component={Link} to="/register" variant="contained" size="small">
                  {t('auth:register.title')}
                </Button>
              )}
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Main Content */}
      <Box sx={{ display: 'flex', flex: 1 }}>
        {/* Left Panel - Academic Professional Design */}
        <Box
          sx={{
            display: { xs: 'none', lg: 'flex' },
            width: { lg: '50%', xl: '55%' },
            position: 'relative',
            overflow: 'hidden',
            bgcolor: '#1F2A44', // Deep Academic Blue
          }}
        >
          {/* Subtle Decorative Elements - NO GRADIENTS */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              overflow: 'hidden',
            }}
          >
            {/* Large Circle - Top Right - Solid Color */}
            <Box
              sx={{
                position: 'absolute',
                top: -100,
                right: -100,
                width: 400,
                height: 400,
                borderRadius: '50%',
                bgcolor: 'rgba(43, 196, 173, 0.08)',
                animation: `${float} 8s ease-in-out infinite`,
              }}
            />

            {/* Medium Circle - Bottom Left - Solid Color */}
            <Box
              sx={{
                position: 'absolute',
                bottom: -80,
                left: -80,
                width: 300,
                height: 300,
                borderRadius: '50%',
                bgcolor: 'rgba(43, 196, 173, 0.05)',
                animation: `${floatSlow} 10s ease-in-out infinite`,
                animationDelay: '1s',
              }}
            />

            {/* Subtle Grid Pattern */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `
                  linear-gradient(rgba(43, 196, 173, 0.03) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(43, 196, 173, 0.03) 1px, transparent 1px)
                `,
                backgroundSize: '50px 50px',
                opacity: 0.5,
              }}
            />
          </Box>

          {/* Content */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              p: { lg: 6, xl: 8 },
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                maxWidth: 480,
                mx: 'auto',
              }}
            >
              {/* Free Credits Badge (Register Only) */}
              {isRegisterPage && (
                <Chip
                  label={t('auth:register.freeCredits.badge')}
                  sx={{
                    mb: 4,
                    bgcolor: 'rgba(43, 196, 173, 0.15)',
                    color: '#2BC4AD',
                    fontWeight: 700,
                    fontSize: '1rem',
                    py: 2.5,
                    px: 2,
                    border: '2px solid',
                    borderColor: '#2BC4AD',
                    '& .MuiChip-label': {
                      px: 2,
                    },
                  }}
                />
              )}

              {/* Main Heading */}
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                  fontSize: { lg: '2.5rem', xl: '3rem' },
                  mb: 3,
                  color: '#FFFFFF',
                }}
              >
                {isRegisterPage
                  ? 'Start Creating Assessments with AI'
                  : 'Welcome Back to NLPask'}
              </Typography>

              {/* Subtitle */}
              <Typography
                variant="body1"
                sx={{
                  color: '#9CA3AF',
                  fontSize: '1.125rem',
                  fontWeight: 400,
                  lineHeight: 1.6,
                  mb: 5,
                }}
              >
                {isRegisterPage
                  ? 'Join thousands of researchers and educators using AI-powered tools for surveys, exams, and quizzes.'
                  : 'Continue building surveys, exams, and quizzes with AI-powered insights.'}
              </Typography>

              {/* Benefits List (Register Only) */}
              {isRegisterPage && (
                <Box
                  sx={{
                    width: '100%',
                    bgcolor: 'rgba(43, 196, 173, 0.08)',
                    borderRadius: 3,
                    p: 4,
                    border: '1px solid rgba(43, 196, 173, 0.2)',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      mb: 3,
                      fontSize: '1rem',
                      color: '#FFFFFF',
                    }}
                  >
                    {t('auth:register.freeCredits.whatYouGet')}
                  </Typography>
                  <Stack spacing={2.5}>
                    {[
                      t('auth:register.freeCredits.benefits.create'),
                      t('auth:register.freeCredits.benefits.questions'),
                      t('auth:register.freeCredits.benefits.responses'),
                      t('auth:register.freeCredits.benefits.analytics'),
                    ].map((benefit, index) => (
                      <Stack key={index} direction="row" spacing={2} alignItems="flex-start">
                        <CheckCircleOutlineIcon
                          sx={{
                            color: '#2BC4AD',
                            fontSize: 24,
                            mt: 0.2,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#9CA3AF',
                            fontSize: '0.95rem',
                            lineHeight: 1.6,
                          }}
                        >
                          {benefit}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Stats (Login Only) */}
              {isLoginPage && (
                <Stack
                  direction="row"
                  spacing={4}
                  sx={{
                    mt: 2,
                  }}
                >
                  {[
                    { label: '10K+', sublabel: 'Active Users' },
                    { label: '50K+', sublabel: 'Surveys Created' },
                    { label: '1M+', sublabel: 'Responses' },
                  ].map((stat, index) => (
                    <Box key={index} sx={{ textAlign: 'center' }}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 800,
                          color: '#2BC4AD',
                          mb: 0.5,
                        }}
                      >
                        {stat.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#9CA3AF',
                          fontSize: '0.85rem',
                        }}
                      >
                        {stat.sublabel}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        </Box>

        {/* Right Panel - Form */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 'calc(100vh - 64px)',
            bgcolor: '#F6F8FB',
          }}
        >
          {/* Free Credits Banner for Mobile (register page only) */}
          {isRegisterPage && (
            <Box
              sx={{
                display: { xs: 'block', lg: 'none' },
                bgcolor: 'white',
                border: '2px solid',
                borderColor: '#2BC4AD',
                borderRadius: 2,
                py: 2,
                px: 3,
                mx: 2,
                mt: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#2BC4AD' }}>
                {t('auth:register.freeCredits.badge')}
              </Typography>
              <Typography variant="caption" sx={{ color: '#4B5563' }}>
                {t('auth:register.freeCredits.description')}
              </Typography>
            </Box>
          )}

          {/* Form Container */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'center',
              p: { xs: 2, sm: 4, lg: 6 },
              overflow: 'auto',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: 440,
                py: { xs: 2, sm: 0 },
              }}
            >
              {children}
            </Box>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'white',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              &copy; {new Date().getFullYear()} NLPask. All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default AuthLayout;
